import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Badge } from "./ui/badge";
import {
  Workflow, RefreshCw, Radio, CheckCircle2, AlertTriangle, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { formatTimeAgo } from "../lib/utils";
import { tenantLocale, tenantTz } from "../lib/company";

function fmtErr(detail) {
  if (!detail) return "Request failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AaWorkformsCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({
    username: "",
    password: "",
    org: "nzaa",
    // ON for a tenant with no saved config: entering working credentials
    // and still receiving no jobs, because a switch defaulted off, was the
    // commonest "it isn't working" report.
    auto_sync: true,
    sync_interval_seconds: 30,
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const refresh = useCallback(async (sync = false) => {
    try {
      const { data } = await api.get("/integration/aa/config");
      setCfg(data);
      if (sync) {
        setForm((f) => ({
          ...f,
          username: data.username || "",
          org: data.org || f.org,
          // Only adopt the stored value once this tenant HAS a stored config;
          // otherwise a fresh tenant's `false` would overwrite the default.
          auto_sync: (data.username || data.password_set) ? !!data.auto_sync : true,
          sync_interval_seconds: data.sync_interval_seconds || 30,
        }));
      }
    } catch (err) {
      if (process.env.NODE_ENV !== "production") {
        console.debug("[AaWorkformsCard] config refresh failed:", err?.message || err);
      }
    }
  }, []);
  useEffect(() => { refresh(true); }, [refresh]);
  useEffect(() => {
    const t = setInterval(() => refresh(false), 10000);
    return () => clearInterval(t);
  }, [refresh]);

  const buildPayload = () => {
    const p = {
      username: form.username.trim(),
      org: form.org.trim() || "nzaa",
      auto_sync: form.auto_sync,
      sync_interval_seconds: Math.max(30, parseInt(form.sync_interval_seconds || 30)),
    };
    if (form.password) p.password = form.password;
    return p;
  };

  // Credentials are checked BEFORE they are stored, so a typo is refused at
  // entry instead of surfacing days later as a silent `last_sync_error`. The
  // test endpoint accepts the unsaved details, so nothing is written on
  // failure. A blank password means "keep the stored one" on both legs.
  const save = async () => {
    setSaving(true);
    try {
      const payload = buildPayload();
      let who = "";
      try {
        const { data: t } = await api.post("/integration/aa/test", payload);
        who = t?.user_name ? ` — signed in as ${t.user_name}` : "";
      } catch (e) {
        toast.error(`AA details not saved — ${fmtErr(e.response?.data?.detail)}`, { duration: 10000 });
        return;
      }
      const { data } = await api.put("/integration/aa/config", payload);
      setCfg(data);
      setForm((f) => ({ ...f, password: "" }));
      toast.success(`AA settings saved${who}`);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/integration/aa/test", buildPayload());
      toast.success(`AA OK — signed in as ${data.user_name}`);
      refresh(false);
      setForm((f) => ({ ...f, password: "" }));
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setTesting(false); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/integration/aa/sync");
      const ac = data.auto_closed?.length || 0;
      toast.success(
        `Synced ${data.entities_synced} entities · ${data.created} new · ${data.updated} updated` +
          (ac ? ` · ${ac} closed` : "")
      );
      refresh(false);
      window.dispatchEvent(new Event("jobs:refresh"));
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setSyncing(false); }
  };

  const statusOk = cfg?.last_sync_status === "ok";
  const statusErr = cfg?.last_sync_status === "error";

  return (
    <section className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid="aa-card">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-900 to-slate-900 text-white">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-md bg-amber-500 flex items-center justify-center">
            <Workflow className="h-5 w-5 text-white" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display font-bold text-lg">AA Roadservice (workforms.link)</div>
            <div className="text-xs text-slate-400">
              NZAA Mobile Data Terminal ·{" "}
              <a
                href="https://workforms.link/Client/Landing"
                target="_blank" rel="noopener noreferrer"
                className="text-amber-300 hover:underline inline-flex items-center gap-0.5"
              >open portal <ExternalLink className="h-3 w-3" /></a>
            </div>
          </div>
        </div>
        <div>
          {cfg?.last_sync_at ? (
            statusOk ? (
              <Badge className="bg-emerald-500/20 text-emerald-300 border-0" data-testid="aa-badge-ok">
                <CheckCircle2 className="h-3 w-3 mr-1" /> Synced {formatTimeAgo(cfg.last_sync_at)}
              </Badge>
            ) : (
              <Badge className="bg-rose-500/20 text-rose-300 border-0" data-testid="aa-badge-err">
                <AlertTriangle className="h-3 w-3 mr-1" /> Error
              </Badge>
            )
          ) : (
            <Badge className="bg-slate-500/20 text-slate-300 border-0" data-testid="aa-badge-idle">Idle</Badge>
          )}
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="uppercase-label">Username</Label>
            <Input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              placeholder="e.g. nts1"
              autoComplete="off"
              data-testid="aa-input-username"
            />
          </div>
          <div>
            <Label className="uppercase-label">Password</Label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              placeholder={cfg?.password_set ? "•••••••••• — leave blank to keep stored" : "Your AA workforms password"}
              autoComplete="new-password"
              data-testid="aa-input-password"
            />
          </div>
          <div>
            <Label className="uppercase-label">Org (Tenant)</Label>
            <Input
              value={form.org}
              onChange={(e) => setForm({ ...form, org: e.target.value })}
              placeholder="nzaa"
              data-testid="aa-input-org"
            />
            <div className="text-xs text-slate-500 mt-1">
              Tenant slug used in the login URL. Stays <code className="bg-slate-100 px-1 rounded">nzaa</code> for NZAA.
            </div>
          </div>
          <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-md px-4 py-3">
            <Switch
              checked={form.auto_sync}
              onCheckedChange={(v) => setForm({ ...form, auto_sync: v })}
              data-testid="aa-auto-switch"
              id="aa-auto"
            />
            <Label htmlFor="aa-auto" className="cursor-pointer">
              <div className="font-semibold text-slate-900 text-sm">Auto-sync</div>
              <div className="text-xs text-slate-500">Poll AA on the interval below.</div>
            </Label>
          </div>
          <div>
            <Label className="uppercase-label">Sync Interval (seconds)</Label>
            <Input
              type="number" min={30}
              value={form.sync_interval_seconds}
              onChange={(e) => setForm({ ...form, sync_interval_seconds: e.target.value })}
              data-testid="aa-input-interval"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2 pt-3 border-t border-slate-100">
          <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="aa-save-btn">
            {saving ? "Saving…" : "Save Settings"}
          </Button>
          <Button variant="outline" onClick={test} disabled={testing} data-testid="aa-test-btn">
            <Radio className="h-4 w-4.5" />
            {testing ? "Testing…" : "Test Connection"}
          </Button>
          <Button variant="outline" onClick={syncNow} disabled={syncing} data-testid="aa-sync-btn">
            <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
            {syncing ? "Syncing…" : "Sync Now"}
          </Button>
        </div>

        {cfg?.last_sync_at && (
          <div
            className={`rounded-md p-3 text-sm border ${
              statusOk ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                       : "bg-rose-50 border-rose-200 text-rose-800"
            }`}
            data-testid="aa-last-sync"
          >
            <div className="font-semibold flex items-center gap-1.5">
              {statusOk ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
              Last sync · {formatTimeAgo(cfg.last_sync_at)} ({new Date(cfg.last_sync_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" })})
            </div>
            {statusOk && (
              <div className="text-xs mt-1">
                {cfg.last_entities_synced} entities fetched · {cfg.last_jobs_created} new
                {cfg.last_jobs_removed > 0 && ` · ${cfg.last_jobs_removed} auto-closed (closed in AA)`}
              </div>
            )}
            {statusErr && cfg.last_sync_error && (
              <div className="text-xs mt-1 font-mono">{cfg.last_sync_error}</div>
            )}
          </div>
        )}

        {/* Session-persistence diagnostics — post Feb 2026 outage.
            Login count should stay ~flat (climb by ~1 per 24h at most,
            not per poll cycle). Anything above that = we're leaking
            sessions again and must stop before AA gets hit. */}
        {(cfg?.shared_client_login_count !== undefined) && (
          <div
            className="rounded-md p-3 text-xs border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 text-slate-700 dark:text-slate-300 font-mono"
            data-testid="aa-session-diagnostics"
          >
            <div className="flex items-center justify-between">
              <span>Session state:</span>
              <span className={cfg.shared_client_is_logged_in
                ? "text-emerald-700 dark:text-emerald-400 font-semibold"
                : "text-slate-500"
              }>
                {cfg.shared_client_is_logged_in ? "authenticated" : "no session"}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span>Logins since process start:</span>
              <span
                className={
                  cfg.shared_client_login_count > 20
                    ? "text-rose-700 dark:text-rose-400 font-bold"
                    : cfg.shared_client_login_count > 5
                    ? "text-amber-700 dark:text-amber-400 font-semibold"
                    : "text-slate-800 dark:text-slate-200 font-semibold"
                }
              >
                {cfg.shared_client_login_count}
              </span>
            </div>
            <div className="flex items-center justify-between mt-1">
              <span>Min poll interval (hard floor):</span>
              <span className="text-slate-800 dark:text-slate-200 font-semibold">
                {cfg.min_poll_interval_seconds ?? 60}s
              </span>
            </div>
            <div className="mt-1.5 text-slate-500 dark:text-slate-500 italic">
              Expected: ~1 login per 24h.
              &gt;5 = investigate. &gt;20 = STOP auto-sync immediately.
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
